// ════════════════════════════════════════════════════════════════════════
//  Eine Schule im Einzelnen
//
//  Vier Dinge auf einer Seite: Stammdaten, Lizenz verkaufen, Historie,
//  Schuladmin einladen. Sie gehören zusammen, weil man sie im Gespräch am
//  Telefon zusammen braucht.
//
//  `license_until` ist hier nirgends ein Eingabefeld. Es kommt aus der
//  Historie - und das Spaltenrecht fehlt ohnehin.
// ════════════════════════════════════════════════════════════════════════

import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useSession } from "@otto/plattform";
import {
  useLicenses,
  useSaveInternals,
  useSchoolPeople,
  useSchools,
  useSellLicense,
  useUpdateSchool,
} from "../lib/data.js";
import {
  LICENSE_LABEL,
  formatDate,
  formatEuro,
  licenseState,
  parseEuroToCents,
} from "../lib/license.js";
import { LicenseForm } from "./LicenseForm.js";
import { SchoolPeople } from "./SchoolPeople.js";
import { InviteSchoolAdmin } from "./InviteSchoolAdmin.js";
import shell from "../../../shared/components/shell.module.css";
import styles from "./schools.module.css";

export function SchoolDetail() {
  const { schoolId = "" } = useParams();
  const { client, state } = useSession();
  const schools = useSchools(client);
  const people = useSchoolPeople(client, schoolId === "" ? null : schoolId);
  const licenses = useLicenses(client, schoolId === "" ? null : schoolId);
  const updateSchool = useUpdateSchool(client);
  const saveInternals = useSaveInternals(client);
  const sell = useSellLicense(client);

  const school = schools.data?.find((s) => s.id === schoolId);
  const userId = state.kind === "signedIn" ? state.identity.user.id : null;

  if (schools.isPending) return <p className={shell.empty}>Einen Moment …</p>;
  if (!school) {
    return (
      <div className={shell.card}>
        <h1 className={shell.title}>Schule nicht gefunden</h1>
        <p className={shell.subtitle}>
          Diese Schule gibt es nicht – oder nicht mehr.{" "}
          <Link to="/schools">Zur Liste</Link>
        </p>
      </div>
    );
  }

  return (
    <>
      <div className={shell.card}>
        <p className={styles.hint}>
          <Link to="/schools">← Alle Schulen</Link>
        </p>
        <h1 className={shell.title}>{school.name}</h1>
        <p className={shell.subtitle}>
          Lizenz bis <strong>{formatDate(school.licenseUntil)}</strong> –{" "}
          <span className={styles.state} data-state={licenseState(school.licenseUntil)}>
            {LICENSE_LABEL[licenseState(school.licenseUntil)]}
          </span>
          . {school.classCount} Klassen, {school.teacherCount} Lehrkräfte,{" "}
          {school.schoolAdminCount} in der Leitung.
        </p>
      </div>

      {/* Direkt unter der Kopfzeile: „wer ist hier Schulleitung“ ist die Frage,
          mit der man diese Seite aufruft – und die Antwort gehört vor das
          Einladeformular weiter unten, nicht dahinter. */}
      <SchoolPeople people={people.data ?? []} pending={people.isPending} />

      <SchoolForm
        key={school.id}
        school={school}
        pending={updateSchool.isPending || saveInternals.isPending}
        error={updateSchool.error?.message ?? saveInternals.error?.message ?? null}
        saved={updateSchool.isSuccess && saveInternals.isSuccess}
        onSave={(input) => {
          updateSchool.mutate({
            id: school.id,
            name: input.name,
            contactName: input.contactName,
            contactEmail: input.contactEmail,
            autoRenew: input.autoRenew,
          });
          saveInternals.mutate({
            schoolId: school.id,
            customerNumber: input.customerNumber,
            annualPriceCents: input.annualPriceCents,
            notes: input.notes,
          });
        }}
      />

      <LicenseForm
        annualPriceCents={school.internals?.annualPriceCents ?? null}
        currentLicenseUntil={school.licenseUntil}
        pending={sell.isPending}
        error={sell.error?.message ?? null}
        onSell={(input) =>
          sell.mutate({
            schoolId: school.id,
            validFrom: input.validFrom,
            validUntil: input.validUntil,
            priceCents: input.priceCents,
            note: input.note,
            createdBy: userId,
          })
        }
      />

      <div className={`${shell.card} ${shell.spaced}`}>
        <h2 className={shell.title} style={{ fontSize: "1.15rem" }}>
          Lizenzhistorie
        </h2>
        <p className={shell.subtitle}>
          Jeder Zeitraum ein Verkauf. Automatisch verlängerte sind als solche
          gekennzeichnet – sie sind eine Zusage und noch kein Zahlungseingang.
        </p>
        {licenses.data?.length === 0 && (
          <p className={shell.empty}>Noch kein Lizenzzeitraum eingetragen.</p>
        )}
        {licenses.data && licenses.data.length > 0 && (
          <div className={styles.scroll}>
            <table className={shell.wide}>
              <thead>
                <tr>
                  <th>Von</th>
                  <th>Bis</th>
                  <th className={styles.numeric}>Betrag</th>
                  <th>Herkunft</th>
                  <th>Abgerechnet</th>
                  {/* Die Notiz ist Freitext und die einzige Spalte hier, die
                      mit mehr Platz etwas anfangen kann. */}
                  <th className={shell.stretch}>Notiz</th>
                </tr>
              </thead>
              <tbody>
                {licenses.data.map((license) => (
                  <tr key={license.id}>
                    <td>{formatDate(license.validFrom)}</td>
                    <td>{formatDate(license.validUntil)}</td>
                    <td className={styles.numeric}>{formatEuro(license.priceCents)}</td>
                    <td>
                      {license.source === "auto" ? (
                        <span className={styles.auto}>automatisch verlängert</span>
                      ) : (
                        "verkauft"
                      )}
                    </td>
                    <td>
                      {license.invoicedAt === null ? (
                        <span className={styles.warn}>offen</span>
                      ) : (
                        formatDate(license.invoicedAt)
                      )}
                    </td>
                    <td className={shell.stretch}>{license.note ?? "–"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <InviteSchoolAdmin schoolId={school.id} schoolName={school.name} />
    </>
  );
}

/**
 * Stammdaten in einem Formular, obwohl sie in zwei Tabellen liegen.
 *
 * Die Trennung `schools` / `school_internals` ist eine Rechtegrenze, keine
 * Aufgabengrenze: wer eine Schule anruft, pflegt Ansprechpartner und
 * Kundennummer im selben Gespräch. Gespeichert wird deshalb in zwei Aufrufen,
 * ausgefüllt in einem Formular.
 */
function SchoolForm({
  school,
  pending,
  error,
  saved,
  onSave,
}: {
  school: {
    name: string;
    contactName: string | null;
    contactEmail: string | null;
    autoRenew: boolean;
    internals: {
      customerNumber: string | null;
      annualPriceCents: number | null;
      notes: string | null;
    } | null;
  };
  pending: boolean;
  error: string | null;
  saved: boolean;
  onSave: (input: {
    name: string;
    contactName: string | null;
    contactEmail: string | null;
    autoRenew: boolean;
    customerNumber: string | null;
    annualPriceCents: number | null;
    notes: string | null;
  }) => void;
}) {
  const [name, setName] = useState(school.name);
  const [contactName, setContactName] = useState(school.contactName ?? "");
  const [contactEmail, setContactEmail] = useState(school.contactEmail ?? "");
  const [autoRenew, setAutoRenew] = useState(school.autoRenew);
  const [customerNumber, setCustomerNumber] = useState(
    school.internals?.customerNumber ?? "",
  );
  const [price, setPrice] = useState(
    school.internals?.annualPriceCents === null ||
      school.internals?.annualPriceCents === undefined
      ? ""
      : (school.internals.annualPriceCents / 100).toFixed(2).replace(".", ","),
  );
  const [notes, setNotes] = useState(school.internals?.notes ?? "");

  const priceCents = parseEuroToCents(price);
  const priceBroken = price.trim() !== "" && priceCents === null;
  const orNull = (value: string) => (value.trim() === "" ? null : value.trim());

  return (
    <div className={`${shell.card} ${shell.spaced}`}>
      <h2 className={shell.title} style={{ fontSize: "1.15rem" }}>
        Stammdaten
      </h2>
      <p className={shell.subtitle}>
        Ansprechpartner sieht die Schule selbst. Kundennummer, Jahrespreis und Notiz sieht
        ausschließlich die Plattform – sie liegen in einer eigenen Tabelle, damit das so
        bleibt.
      </p>

      {saved && <div className={`${shell.notice} ${shell.noticeGood}`}>Gespeichert.</div>}
      {error && (
        <div className={`${shell.notice} ${shell.noticeError}`}>
          Nicht gespeichert: {error}
        </div>
      )}

      <form
        onSubmit={(event) => {
          event.preventDefault();
          if (priceBroken) return;
          onSave({
            name: name.trim(),
            contactName: orNull(contactName),
            contactEmail: orNull(contactEmail),
            autoRenew,
            customerNumber: orNull(customerNumber),
            annualPriceCents: priceCents,
            notes: orNull(notes),
          });
        }}
      >
        <div className={styles.columns}>
          <div>
            <label htmlFor="school-name">Name der Schule</label>
            <input
              id="school-name"
              className={shell.field}
              value={name}
              onChange={(event) => setName(event.target.value)}
              required
            />
          </div>
          <div>
            <label htmlFor="contact-name">Ansprechpartner</label>
            <input
              id="contact-name"
              className={shell.field}
              value={contactName}
              onChange={(event) => setContactName(event.target.value)}
              placeholder="Frau Dr. Kern"
            />
          </div>
          <div>
            <label htmlFor="contact-email">E-Mail des Ansprechpartners</label>
            <input
              id="contact-email"
              className={shell.field}
              type="email"
              value={contactEmail}
              onChange={(event) => setContactEmail(event.target.value)}
              placeholder="leitung@schule.de"
            />
          </div>
          <div>
            <label htmlFor="customer-number">Kundennummer</label>
            <input
              id="customer-number"
              className={shell.field}
              value={customerNumber}
              onChange={(event) => setCustomerNumber(event.target.value)}
              placeholder="aus der Buchhaltung"
            />
          </div>
          <div>
            <label htmlFor="annual-price">Jahrespreis</label>
            <input
              id="annual-price"
              className={shell.field}
              value={price}
              onChange={(event) => setPrice(event.target.value)}
              placeholder="240,00"
              inputMode="decimal"
            />
          </div>
        </div>

        <div className={shell.spaced}>
          <label htmlFor="notes">Interne Notiz</label>
          <textarea
            id="notes"
            className={`${shell.field} ${shell.multiline}`}
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            rows={3}
            placeholder="Was man beim nächsten Anruf wissen sollte."
          />
        </div>

        <div className={`${styles.checkbox} ${shell.spaced}`}>
          <input
            id="auto-renew"
            type="checkbox"
            checked={autoRenew}
            onChange={(event) => setAutoRenew(event.target.checked)}
          />
          <label htmlFor="auto-renew">
            Lizenz automatisch verlängern (31.08. → 31.08., zum Jahrespreis)
          </label>
        </div>

        {priceBroken && (
          <div className={`${shell.notice} ${shell.noticeError}`}>
            Der Jahrespreis muss ein Betrag sein, zum Beispiel 240,00.
          </div>
        )}

        <div className={styles.actions}>
          <button
            type="submit"
            className={shell.button}
            disabled={pending || name.trim() === "" || priceBroken}
          >
            {pending ? "Wird gespeichert …" : "Stammdaten speichern"}
          </button>
        </div>
      </form>
    </div>
  );
}
